import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useState } from 'react';
import { useModelResetTimer, useUpdateModelResetTimer, useCreateModelResetTimer } from '../../../lib/supabase/formats/supabaseModelResetTimesService';
import { toast } from 'react-hot-toast';
import { DateTime } from 'luxon';

interface ResetTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  model_id: string;
  canEdit: boolean;
}

export const ResetTimeDialog = ({ open, onOpenChange, model_id, canEdit }: ResetTimeDialogProps) => {
  const { data: resetTimer } = useModelResetTimer(model_id);
  const { mutateAsync: updateTimer } = useUpdateModelResetTimer();
  const { mutateAsync: createTimer } = useCreateModelResetTimer();
  const [time, setTime] = useState(resetTimer?.time ? resetTimer.time.toFormat('HH:mm:ss') : '');

  const handleSave = async () => {
    if (!time) {
      toast.error('Please enter a reset time');
      return;
    }

    try {
      // Convert time string to DateTime
      const [hoursStr, minutesStr, secondsStr = '00'] = time.split(':');
      const hours = parseInt(hoursStr);
      const minutes = parseInt(minutesStr);
      const seconds = parseInt(secondsStr);
      
      // Create a DateTime in BST
      const timeValue = DateTime.now()
        .setZone('Europe/London')
        .set({
          hour: hours,
          minute: minutes,
          second: seconds,
          millisecond: 0
        });

      if (resetTimer) {
        await updateTimer({
          model_id,
          timer: { time: timeValue }
        });
      } else {
        await createTimer({
          model_id,
          timer: {
            model_id: model_id,
            time: timeValue
          }
        });
      }
      toast.success('Reset time updated successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update reset time', error);
      toast.error('Failed to update reset time');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set Reset Time</DialogTitle>
          <DialogDescription>
            Set the time when the model's schedule should automatically reset each day.
            The time will be stored in UK time (BST/GMT).
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 white-input dark:white-input">
          <Input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            disabled={!canEdit}
            step="1"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canEdit}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 